import { Block } from '@chameleoncreator/creator-app';
import { addHeaderBlocksToStateAction } from 'actions/addHeaderBlocksToStateAction';
import { preprocessModuleAction } from 'actions/preprocessModuleAction';
import { rehydrateAction } from 'actions/rehydrate';
import { updateThemeHeadersAction } from 'actions/updateThemeHeadersAction';
import { createModule } from 'factories/module';
import { flatten } from 'factories/utils';
import { addHeaderBlocksToState } from 'reducers/addHeaderBlocksToState';
import { addScormResponse } from 'reducers/addScormResponse';
import { audioAcknowledged } from 'reducers/audioAcknowledged';
import { controlAudio } from 'reducers/controlAudio';
import { focusBlock } from 'reducers/focusBlock';
import { hideHeader } from 'reducers/hideHeader';
import { loadBookmarkData } from 'reducers/loadBookmarkData';
import { loadSuspendData } from 'reducers/loadSuspendData';
import { mirrorActive } from 'reducers/mirrorActive';
import { preprocessModule } from 'reducers/preprocessModule';
import { rehydrate } from 'reducers/rehydrate';
import { rehydrateFromLMS } from 'reducers/rehydrateFromLMS';
import { removeBlock } from 'reducers/removeBlock';
import { removeBlockLocal } from 'reducers/removeBlockLocal';
import { setActiveTopic } from 'reducers/setActiveTopic';
import { setAPIError } from 'reducers/setAPIError';
import { setBlock } from 'reducers/setBlock';
import { setBlocks } from 'reducers/setBlocks';
import { setCurrentInteraction } from 'reducers/setCurrentInteraction';
import { setError } from 'reducers/setError';
import { setExit } from 'reducers/setExit';
import { setExitComplete } from 'reducers/setExitComplete';
import { setFonts } from 'reducers/setFonts';
import { setIsScrolling } from 'reducers/setIsScrolling';
import { setLinks } from 'reducers/setLinks';
import { setProjectId } from 'reducers/setProjectId';
import { setRootBlock } from 'reducers/setRootBlock';
import { setTheme } from 'reducers/setTheme';
import { setThemeId } from 'reducers/setThemeId';
import { setTracker } from 'reducers/setTracker';
import { updateAssessment } from 'reducers/updateAssessment';
import { updateCourseTheme } from 'reducers/updateCourseTheme';
import { updateInteractionsOpen } from 'reducers/updateInteractionsOpen';
import { updateInteractionsSeen } from 'reducers/updateInteractionsSeen';
import { updateRecoloredChameleonImages } from 'reducers/updateRecoloredChameleonImages';
import { updateScore } from 'reducers/updateScore';
import { updateScormSettings } from 'reducers/updateScormSettigns';
import { updateUserPalette } from 'reducers/updateUserPallete';
import { updateInteractionsLocked } from 'reducers/updateInteractionsLocked';
import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { getTopicGroup } from 'selectors/block';
import { addFeedBackData } from '../reducers/addFeedBackData';
import { getLocalState, getState, watch } from './localStore';
import { Action, initialState, State } from './store';
import { updateLegacyBlocksWithSId } from 'services/updateLegacyBlocksWithSId';
import { addAdvancedEditingBlocksToStateAction } from 'actions/addAdvancedEditingBlocksToStateAction';
import { addAdvancedEditingBlocksToState } from 'reducers/addAdvancedEditingBlocksToState';

const hasMode = (mode: string): boolean => {
  const w = window.hasMode && window.hasMode(mode);
  return w;
};

export const remoteable = (): boolean => {
  return hasMode('EDIT');
};

remoteable();

if (hasMode('DEVELOPMENT')) {
  const mod = createModule('Example');
  const [flatMod, rootBlockId] = flatten(mod);
  const cur = (getTopicGroup(flatMod).children![0] as Block).id;

  initialState.blocks = flatMod;
  initialState.rootBlockId = rootBlockId;
  initialState.currentTopic = cur;
}

const actionHandlers = {
  ADD_SCORM_RESPONSE: addScormResponse,
  FOCUS_BLOCK: focusBlock,
  HIDE_HEADER: hideHeader,
  LOAD_BOOKMARK_DATA: loadBookmarkData,
  LOAD_SUSPEND_DATA: loadSuspendData,
  MIRROR_ACTIVE: mirrorActive,
  PREPROCESS_MODULE: preprocessModule,
  REHYDRATE: rehydrate,
  REHYDRATE_FROM_LMS: rehydrateFromLMS,
  REMOVE_BLOCK: removeBlock,
  REMOVE_BLOCK_LOCAL: removeBlockLocal,
  SET_ACTIVE_TOPIC: setActiveTopic,
  SET_API_ERROR: setAPIError,
  SET_BLOCK: setBlock,
  SET_BLOCKS: setBlocks,
  SET_CURRENT_INTERACTION: setCurrentInteraction,
  SET_ERROR: setError,
  SET_EXIT: setExit,
  SET_EXIT_COMPLETE: setExitComplete,
  SET_FONTS: setFonts,
  SET_IS_SCROLLING: setIsScrolling,
  SET_LINKS: setLinks,
  SET_PROJECT_ID: setProjectId,
  SET_ROOT_BLOCK: setRootBlock,
  SET_THEME: setTheme,
  SET_THEME_ID: setThemeId,
  SET_TRACKER: setTracker,
  SUBMIT_FEEDBACK: addFeedBackData,
  UPDATE_COURSE_THEME: updateCourseTheme,
  UPDATE_INTERACTIONS_OPEN: updateInteractionsOpen,
  UPDATE_INTERACTIONS_SEEN: updateInteractionsSeen,
  UPDATE_RECOLORED_CHAMELEON_IMAGES: updateRecoloredChameleonImages,
  UPDATE_SCORE: updateScore,
  UPDATE_SCORM_SETTINGS: updateScormSettings,
  UPDATE_USER_PALETTE: updateUserPalette,
  UPDATE_ASSESSMENT: updateAssessment,
  CONTROL_AUDIO: controlAudio,
  AUDIO_ACKNOWLEDGED: audioAcknowledged,
  ADD_HEADER_BLOCKS_TO_STATE: addHeaderBlocksToState,
  ADD_ADVANCED_EDITING_BLOCKS_TO_STATE: addAdvancedEditingBlocksToState,
  UPDATE_INTERACTIONS_LOCKED: updateInteractionsLocked,
};

// translate actions into handlers (standard redux reducer)
export const reducer = (state: State = initialState, action: Action): State => {
  const handler = actionHandlers[action.type];
  return handler ? handler(state, action) : { ...state };
};

// create the store from the reducer above

const composeEnhancers =
  window['__REDUX_DEVTOOLS_EXTENSION_COMPOSE__'] || compose;

const store = createStore(reducer, composeEnhancers(applyMiddleware(thunk)));

(() => {
  if (hasMode('PREVIEW')) {
    const savedStore = getState();

    // if we have saved state, lets load it in.
    if (savedStore && savedStore !== undefined) {
      store.dispatch(rehydrateAction(savedStore));
      store.dispatch(preprocessModuleAction());
      //
      updateThemeHeadersAction(store.dispatch, store.getState);
    }

    // needed to move this out of the savedStore checks for local dev
    addHeaderBlocksToStateAction(store.dispatch, store.getState);
  }

  if (hasMode('DEVELOPMENT')) {
    //

    // rehydrate
    const savedStore = getLocalState();

    // if we have saved state, lets load it in.
    if (savedStore && savedStore !== undefined) {
      store.dispatch(rehydrateAction(savedStore));
      store.dispatch(preprocessModuleAction());
      //
      updateThemeHeadersAction &&
        updateThemeHeadersAction(store.dispatch, store.getState);
    }

    // needed to move this out of the savedStore checks for local dev
    addHeaderBlocksToStateAction(store.dispatch, store.getState);

    addAdvancedEditingBlocksToStateAction()(store.dispatch, store.getState);

    updateLegacyBlocksWithSId(store);
    //Save changes
    watch(store as any);
  }
})();

export default store;
